import { Armchair } from "lucide-react";

import { Card, CardContent } from "components/ui/card";

import { DEFAULT_TRACK_COLOR } from "../event-experience/AgendaSchedule";
import { useTracks } from "../event-experience/use-event-content";
import { TrackChampions } from "../event-experience/YourTrackBanner";
import { TrackSwitchControl } from "./TrackSwitchControl";
import type { AttendeeTrackSwitch } from "./use-attendee-track-switch";

/**
 * The attendee's theme table (their track) on the Me tab, with the way
 * to ask for another one (TNG-92). Shown only while the organiser has theme
 * table requests on and the event has tracks: an event without tracks has no
 * theme tables, so the card would only say "none". "Theme table" keeps it
 * apart from the dinner table cards beside it (YourSeatingCards).
 *
 * It is also where the track's champions show while requests are on, so the
 * Me tab names the track once: without requests the page shows YourTrackCard
 * instead, which draws the same name and champions.
 */
export function YourTableCard({
  eventId,
  trackSwitch,
}: {
  eventId: string;
  trackSwitch: AttendeeTrackSwitch;
}) {
  const tracks = useTracks(eventId);
  if (!trackSwitch.enabled || tracks.status !== "success") return null;
  if (tracks.data.length === 0) return null;
  const myTrack = tracks.data.find(
    (track) => track.id === trackSwitch.myTrackId,
  );

  return (
    <Card className="border-border" data-testid="your-table">
      <CardContent className="space-y-3 py-4">
        <div className="flex items-center gap-3">
          <Armchair
            className="h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <p className="min-w-0 text-foreground">
            {myTrack ? (
              <>
                Your theme table:{" "}
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{
                      backgroundColor: myTrack.color ?? DEFAULT_TRACK_COLOR,
                    }}
                    aria-hidden
                  />
                  <span data-testid="your-table-name">{myTrack.name}</span>
                </span>
              </>
            ) : (
              <span data-testid="your-table-none">
                You do not have a theme table yet.
              </span>
            )}
          </p>
        </div>
        {myTrack && (
          <TrackChampions
            eventId={eventId}
            track={myTrack}
            testIdPrefix="your-table"
          />
        )}
        <TrackSwitchControl
          eventId={eventId}
          tracks={tracks.data}
          currentTrackId={trackSwitch.myTrackId}
          request={trackSwitch.request}
          writes={trackSwitch.writes}
          accepting={trackSwitch.accepting}
        />
      </CardContent>
    </Card>
  );
}
